import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
} from '@mui/material';
import { FolderSpecial, Assignment, CheckCircle, Warning } from '@mui/icons-material';
import { projectsAPI, tasksAPI } from '../services/api';
import Layout from '../components/Layout/Layout';

const Dashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalProjects: 0,
    totalTasks: 0,
    completedTasks: 0,
    overdueTasks: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const projectsRes = await projectsAPI.getAll();
      const projects = projectsRes.data.data;

      let totalTasks = 0;
      let completedTasks = 0;
      let overdueTasks = 0;

      for (const project of projects) {
        const tasksRes = await tasksAPI.getProjectTasks(project._id);
        const tasks = tasksRes.data.data;
        
        totalTasks += tasks.length;
        completedTasks += tasks.filter((t) => t.status === 'completed').length;
        overdueTasks += tasks.filter(
          (t) => new Date(t.deadline) < new Date() && t.status !== 'completed'
        ).length;
      }

      setStats({
        totalProjects: projects.length,
        totalTasks,
        completedTasks,
        overdueTasks,
      });
    } catch (err) {
      console.error('Failed to fetch dashboard data', err);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: 'Total Projects',
      value: stats.totalProjects,
      icon: <FolderSpecial sx={{ fontSize: 40 }} />,
      color: '#1976d2',
      onClick: () => navigate('/projects'),
    },
    {
      title: 'Total Tasks',
      value: stats.totalTasks,
      icon: <Assignment sx={{ fontSize: 40 }} />,
      color: '#9c27b0',
    },
    {
      title: 'Completed Tasks',
      value: stats.completedTasks,
      icon: <CheckCircle sx={{ fontSize: 40 }} />,
      color: '#4caf50',
    },
    {
      title: 'Overdue Tasks',
      value: stats.overdueTasks,
      icon: <Warning sx={{ fontSize: 40 }} />,
      color: '#f44336',
    },
  ];

  if (loading) {
    return (
      <Layout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box>
        <Typography variant="h4" gutterBottom>
          Dashboard
        </Typography>

        <Grid container spacing={3} mt={2}>
          {statCards.map((stat, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card
                sx={{
                  cursor: stat.onClick ? 'pointer' : 'default',
                  '&:hover': stat.onClick ? { boxShadow: 4 } : {},
                }}
                onClick={stat.onClick}
              >
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography variant="h4" fontWeight="bold">
                        {stat.value}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {stat.title}
                      </Typography>
                    </Box>
                    <Box sx={{ color: stat.color }}>{stat.icon}</Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Layout>
  );
};

export default Dashboard;
